import React, { useRef, useState } from "react";
import { useHistory, useParams } from "react-router";

// collections
import EquipmentRequestForm from "./collections/EquipmentRequest/EquipmentRequest.form";
import { useMutation } from "@apollo/client";
import { CREATE_EQUIPMENT_REQUEST } from "../../../graphql/equipmentRequest/equipmentRequest.mutations";
import ActionButton from "../../../components/buttons/ActionButton/ActionButton.component";
import { Grid, Container, Typography, Alert, Box } from "@mui/material";

import { validate } from "../../../shared/utility";
import { createRequestSubmitData } from "./NewEquipmentRequest.utils";

const validationSchema = {
  job: {
    type: "input",
    validation: {
      required: true,
    },
  },
  truck: {
    type: "input",
    validation: {
      required: true,
    },
  },
  equipment: {
    type: "list",
    fields: {
      requestQty: {
        type: "input",
        validation: {
          required: true,
          isNumeric: true,
        },
      },
    },
  },
};

export default function NewEquipmentRequest() {
  const [errors, setErrors] = useState({ equipment: [] });
  const [message, setMessage] = useState("");
  const [messageSeverity, setMessageSeverity] = useState("success"); // 'success' or 'error'
  const [showMessage, setShowMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const history = useHistory();
  const [createEquipmentRequest] = useMutation(CREATE_EQUIPMENT_REQUEST);
  const currentForm = useRef();
  const { jobId } = useParams();

  const displayMessage = (messageText, severity = "success") => {
    setMessage(messageText);
    setMessageSeverity(severity);
    setShowMessage(true);
  };

  const hideMessage = () => {
    setShowMessage(false);
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (currentForm.current && currentForm.current.getData) {
      const finalData = currentForm.current.getData();

      const { isValid, errors } = validate(finalData, validationSchema);

      if (isValid) {
        setIsSubmitting(true);
        // Hide any previous messages when starting new submission
        setShowMessage(false);

        createEquipmentRequest({
          variables: { ...createRequestSubmitData(finalData) },
          refetchQueries: [],
        })
          .then((result) => {
            if (result && result.data) {
              // Success case - Equipment request created successfully
              displayMessage(
                "Equipment request created successfully!",
                "success"
              );
              setIsSubmitting(false);

              // Redirect after a longer delay so user can see the success message
              setTimeout(() => {
                history.push("/requests");
              }, 4000); // Increased from 1500ms to 4000ms (4 seconds)
            }
          })
          .catch((error) => {
            console.error("Mutation error:", error);
            setIsSubmitting(false);

            // Handle different error types
            const errorMessage =
              error.message ||
              "Something went wrong while creating the equipment request";

            if (errorMessage.includes("crew member profile")) {
              // Specific error for missing Crew Member
              displayMessage(errorMessage, "error");
            } else if (errorMessage.includes("Access Not Allowed")) {
              // Permission error
              displayMessage(
                "You do not have permission to create equipment requests.",
                "error"
              );
            } else if (errorMessage.includes("Authentication")) {
              // Authentication error
              displayMessage(
                "Please log in to create equipment requests.",
                "error"
              );
            } else {
              // Generic error
              displayMessage(
                `Failed to create equipment request: ${errorMessage}`,
                "error"
              );
            }

            setErrors((prev) => ({ ...prev, __server: errorMessage }));
          });
      } else {
        // Only set errors, don't show message
        setErrors(errors);
        // Removed: displayMessage('Please fix the form errors before submitting.', 'error')
      }
    } else {
      console.error("ERROR: Form does not have the forwardRef set.");
      displayMessage("Form error: Unable to submit request.", "error");
    }
  };

  return (
    <div>
      <Container disableGutters sx={{ marginBottom: 4 }}>
        <Grid container>
          <Grid item>
            <Typography variant="h2">New Equipment Request</Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Message Display at the TOP of the form */}
      {showMessage && (
        <Box sx={{ marginBottom: 3 }}>
          <Alert
            severity={messageSeverity}
            variant="filled"
            onClose={hideMessage}
            sx={{
              width: "90%",
              "& .MuiAlert-message": {
                fontSize: "1rem",
                fontWeight: "500",
              },
            }}
          >
            {message}
          </Alert>
        </Box>
      )}

      <EquipmentRequestForm ref={currentForm} errors={errors} id={jobId} />

      <Container disableGutters sx={{ marginBottom: 4, marginTop: 4 }}>
        <Grid container spacing={2}>
          <Grid item>
            <ActionButton
              label="Cancel"
              color="secondary"
              onClick={() => history.goBack()}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item>
            <ActionButton
              label={
                isSubmitting ? "Submitting..." : "Submit Equipment Request"
              }
              color="success"
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}
